import { store } from 'app/store';
import { IUser } from 'features/user/userAPI';

// Color schemes for the colors in lists
export type PDColor = {
  name: string;
  hex: string;
};
export type PDPalet = {
  red: PDColor;
  orange: PDColor;
  yellow: PDColor;
  green: PDColor;
  blue: PDColor;
  purple: PDColor;
  grey: PDColor;
  black: PDColor;
  white: PDColor;
  none: PDColor;
  me: PDColor;
};

// Returns the <i>-th distinct color in a range of size <num>.
export function getColorInRange(
  i: number,
  num: number,
  saturation: number = 100,
  lightness: number = 30,
  asHex?: boolean,
): string | undefined {
  if (i < 0) return undefined;
  const hue = (i / num) * 360; // Spread hues evenly
  const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  return asHex ? hslToHex(color) : color;
}

// Returns a color that is unique for <id> in the given set of id's.
// For author names in bold on a light grey background,
// saturation 100 and lightness 30 are a good choice.
export function getColorForId(
  id: number,
  ids: number[] = [],
  props?: {
    saturation?: number; // 0-100
    lightness?: number; // 0-100
    asHex?: boolean;
  },
): string | undefined {
  const {
    saturation = 100, // 0-100
    lightness = 20, // 0-100
    asHex = false,
  } = props ?? {};
  let idList: number[] = ids;
  if (idList.length === 0) {
    const dlg = store.getState().dialogue?.dialogue;
    if (dlg) idList = dlg.subscribers?.map((s: IUser) => s.id) ?? [];
  }
  const num = idList.length;
  const i = idList.findIndex((x) => x === id);
  const color =
    i >= 0 ? getColorInRange(i, num, saturation, lightness, asHex) : '#CCCCCC';
  return color;
}

// Returns <num> distinct colors.
export function getColorRange(
  num: number,
  saturation: number = 100, // 0-100
  lightness: number = 20, // 0-100
): string[] {
  const col: string[] = [];
  for (var i = 0; i < num; i++) {
    col.push(getColorInRange(i, num, saturation, lightness) || '');
  }
  return col;
}

const toHex = (value: number): string => {
  const hex = Math.round(value * 255).toString(16);
  return hex.length === 1 ? '0' + hex : hex;
};

export function hslToHex(hsl: string): string {
  const hslRegex = /^hsl\(\s*(\d+),\s*(\d+)%?,\s*(\d+)%?\)$/;
  const result = hsl.match(hslRegex);
  if (!result) throw new Error('Invalid HSL format');
  const h = parseInt(result[1]);
  const s = parseInt(result[2]) / 100;
  const l = parseInt(result[3]) / 100;
  // Convert HSL to RGB
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0,
    g = 0,
    b = 0;
  if (0 <= h && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (300 <= h && h < 360) {
    r = c;
    g = 0;
    b = x;
  }
  r = r + m;
  g = g + m;
  b = b + m;
  return `${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Other color related functions
export function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (val: number) => Math.max(0, Math.min(255, val));
  const toHex = (val: number) => clamp(val).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function contrastingTextColor(hex: string): 'white' | 'black' {
  // Remove "#" if present
  hex = hex.replace(/^#/, '');
  // Expand shorthand form (#abc → #aabbcc)
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((ch) => ch + ch)
      .join('');
  }
  if (hex.length !== 6) {
    throw new Error('Invalid hex color.');
  }
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  // Calculate luminance using the W3C formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  // If luminance is high, return black text, else white text
  return luminance > 0.5 ? 'black' : 'white';
}
